import { useEffect, useLayoutEffect, useRef } from 'react'

import { EMPTY_OBJECT } from '../../../../constants/common'
import { CENTRIFUGE_LOG_PREFIX } from '../../Centrifuge.constants'
import { useCentrifugeConnection } from '../useCentrifugeConnection'

/**
 * Subscribe to a Centrifugo channel for the lifetime of the calling
 * component. Opt-in via `enabled`; pass `null`/empty `channel` to skip.
 *
 * Internally opts into the shared connection, so consumers don't need to
 * call `useCentrifugeConnection` themselves. The shared connection opens on
 * the first opt-in across the app and closes when the last consumer drops.
 *
 * Handlers are captured into a ref (mirroring `useEventListener`), so callers
 * don't have to memoize them — the subscription is only rebuilt when
 * `channel` or `enabled` change, not on every render.
 *
 * Cleanup order matters: `unsubscribe()` first so the SDK can emit the
 * final `unsubscribed` event into our listener, then detach handlers, then
 * remove the subscription from the client registry.
 *
 * @param {{
 *   channel: string | null,
 *   enabled?: boolean,
 *   onPublication?: (ctx: any) => void,
 *   onSubscribed?: (ctx: any) => void,
 *   onSubscribing?: (ctx: any) => void,
 *   onUnsubscribed?: (ctx: any) => void,
 *   onError?: (ctx: any) => void,
 * }} options
 */
export function useChannelSubscription(options = EMPTY_OBJECT) {
  const { channel, enabled = true } = options

  const isActive = Boolean(enabled && channel)
  const client = useCentrifugeConnection({ enabled: isActive })

  const optionsRef = useRef(options)

  useLayoutEffect(() => {
    optionsRef.current = options
  }, [options])

  useEffect(() => {
    if (client && isActive) {
      const subscription = client.newSubscription(channel)

      function handleSubscribing(ctx) {
        optionsRef.current.onSubscribing?.(ctx)
      }

      function handleSubscribed(ctx) {
        optionsRef.current.onSubscribed?.(ctx)
      }

      function handleUnsubscribed(ctx) {
        optionsRef.current.onUnsubscribed?.(ctx)
      }

      function handlePublication(ctx) {
        optionsRef.current.onPublication?.(ctx)
      }

      function handleError(ctx) {
        optionsRef.current.onError?.(ctx)
      }

      subscription.on('subscribing', handleSubscribing)
      subscription.on('subscribed', handleSubscribed)
      subscription.on('unsubscribed', handleUnsubscribed)
      subscription.on('publication', handlePublication)
      subscription.on('error', handleError)

      subscription.subscribe()

      return () => {
        try {
          subscription.unsubscribe()
        } catch (error) {
          console.warn(
            `${CENTRIFUGE_LOG_PREFIX} subscription cleanup error`,
            error
          )
        }

        subscription.off('subscribing', handleSubscribing)
        subscription.off('subscribed', handleSubscribed)
        subscription.off('unsubscribed', handleUnsubscribed)
        subscription.off('publication', handlePublication)
        subscription.off('error', handleError)

        client.removeSubscription(subscription)
      }
    }
  }, [channel, client, isActive])
}
